// A log you can read and copy from a phone.
//
// Shown when the URL carries ?debug=1, in either view. Deliberately available
// on the guest route too: the camera problem only happens on a real phone, and
// that is the customer's screen.

import { useEffect, useState, useSyncExternalStore } from 'react';
import { debugLog, debugReport, type LogEntry } from '../debug';

// A stable empty snapshot, for the same identity reason as the store's.
const NO_ENTRIES: readonly LogEntry[] = [];

export function DebugPanel() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const entries = useSyncExternalStore(debugLog.subscribe, debugLog.entries, () => NO_ENTRIES);

  const errorCount = entries.filter((entry) => entry.level === 'error').length;

  // Opening on the first error means you are not asked to notice a badge while
  // holding a phone up to a wall.
  const hasErrors = errorCount > 0;
  useEffect(() => {
    if (hasErrors) setOpen(true);
  }, [hasErrors]);

  const copy = async () => {
    const report = debugReport();
    try {
      await navigator.clipboard.writeText(report);
      setCopied('Copied to clipboard');
    } catch {
      // Clipboard is blocked in plenty of mobile contexts. Select it instead so
      // it can be copied by hand rather than lost.
      const box = document.getElementById('debug-report') as HTMLTextAreaElement | null;
      box?.focus();
      box?.select();
      setCopied('Select all, then copy');
    }
    setTimeout(() => setCopied(null), 4000);
  };

  if (!open) {
    return (
      <button type="button" className="debugtoggle" onClick={() => setOpen(true)}>
        Debug{errorCount > 0 ? ` (${errorCount})` : ''}
      </button>
    );
  }

  return (
    <div className="debugpanel">
      <div className="debugpanel__bar">
        <strong>Debug</strong>
        <span className="debugpanel__build">{__BUILD_ID__}</span>
        <button type="button" className="ghostbtn" onClick={copy}>
          Copy
        </button>
        <button type="button" className="ghostbtn" onClick={() => debugLog.clear()}>
          Clear
        </button>
        <button type="button" className="ghostbtn" onClick={() => setOpen(false)}>
          Close
        </button>
      </div>

      {copied && <p className="debugpanel__copied">{copied}</p>}

      {/* The report also lives in a textarea, so it can be selected by hand
          where the clipboard API is unavailable. */}
      <textarea id="debug-report" className="debugpanel__report" readOnly value={debugReport()} />

      <div className="debugpanel__log">
        {entries.length === 0 && <p className="empty-note">Nothing logged yet.</p>}
        {entries.map((entry, i) => (
          <p key={i} className={`debugpanel__line is-${entry.level}`}>
            <span>{new Date(entry.at).toISOString().slice(11, 19)}</span> {entry.message}
          </p>
        ))}
      </div>
    </div>
  );
}
